import React from "react";
import loc from "owa-localize";
import { observer } from "owa-mobx-react";
import { KeytipManager, type IKeytipLayerProps } from "@fluentui/react";
import { KeytipLayer } from "@fluentui/react/lib/KeytipLayer";
import { isHostAppFeatureEnabled } from "owa-hostapp-feature-flags";
import { isGulping } from "owa-config/lib/bootstrapOptions";
import { keytipAriaDescription } from "./RibbonKeytipLayer.locstring.json";
import { useValueInitializedOnce } from "owa-react-hooks/lib/useValueInitializedOnce";
import { useCustomKeytipEvents } from "./useCustomKeytipEvents";
import { onEnterKeytipMode, onExitKeytipMode } from "owa-command-ribbon-keytip-utils";
import { isMac } from "owa-user-agent/lib/userAgent";
/* eslint-disable-next-line @typescript-eslint/no-restricted-imports -- Dev-only compatibility host for Fluent v8 keytips until the ribbon moves to Fluent v9. */
import { render } from "owa-react-dom";

const getContent = (): IKeytipLayerProps["content"] => {
    if (isMac()) {
        return "ctrl+.";
    }
    return isHostAppFeatureEnabled("altForKeytips")
        ? loc(keytipAriaDescription, "Alt", "F10")
        : "Alt";
};

export interface RibbonKeytipLayerProps {
    selectedTabId: string;
}

const shouldUseLegacyKeytipLayerInDev = (): boolean => isGulping();

// ADO 342613, use the legacy keytip layer in dev environments until the ribbon moves to Fluent v9
const DevLegacyRibbonKeytipLayer: React.FunctionComponent<RibbonKeytipLayerProps> = observer(({ selectedTabId }: RibbonKeytipLayerProps) => {
    const content = getContent();
    
    React.useEffect(() => {
        const container = document.createElement("div");
        document.body.appendChild(container);
        
        const unmount = render(<KeytipLayer content={content} onEnterKeytipMode={onEnterKeytipMode(selectedTabId)} onExitKeytipMode={onExitKeytipMode}/>, container, undefined /* callback */, true /* forceLegacyRender */);
        
        return () => {
            unmount();
            container.remove();
        };
    }, [selectedTabId, content]);
    
    return null;
}, "DevLegacyRibbonKeytipLayer");

export const RibbonKeytipLayer: React.FunctionComponent<RibbonKeytipLayerProps> = observer(({ selectedTabId }: RibbonKeytipLayerProps) => {
    useValueInitializedOnce(() => {
        // Delay registration of keytips until user is in keytip mode
        KeytipManager.getInstance().init(true);
    });
    
    useCustomKeytipEvents(isHostAppFeatureEnabled("altForKeytips"));
    
    if (shouldUseLegacyKeytipLayerInDev()) {
        return <DevLegacyRibbonKeytipLayer selectedTabId={selectedTabId}/>;
    }
    
    return (<KeytipLayer content={getContent()} onEnterKeytipMode={onEnterKeytipMode(selectedTabId)} onExitKeytipMode={onExitKeytipMode}/>);
}, "RibbonKeytipLayer");
